/* eslint-disable indent */
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import {
    CreateRentaInput,
    TiposPagos,
    useCreateRentaMutation,
    useDescontarSaldoEasyrewardsMutation,
    useTarifasLazyQuery,
} from "src/gql/schema"
import { PAYMENT_METHODS } from "src/constants/payments"
import { useProfile } from "src/shared/hooks/useProfile"
import { useDate } from "src/shared/hooks/useDate"
import useSnackbar from "src/shared/hooks/useSnackbar"
import { addDays } from "src/shared/helpers/addDays"
import { sum } from "src/shared/helpers/calculator"
import { getPaymentList } from "src/shared/sections/payment/Payment.helpers"
import { toggleRoomDetailsDrawer } from "src/store/navigation/navigationSlice"
import useValidarLovePoints from "../../hooks/useValidarLovePoints"
import { filtrarTarifasRenta } from "../../helpers/filtrarTarifasRenta"
import { VentaHabitacionCase } from "../VentaHabitacionCase.types"

// Venta de mostrador: se crea una renta nueva con cualquier tarifa activa de renta
const useRentaCase = (): VentaHabitacionCase => {
    const { habitacion_id = "" } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { hotel_id, turno_hotel_id, usuario_id } = useProfile()
    const { localDateToUTCString, diffDays, setHHMMSS } = useDate()
    const { showSnackbar } = useSnackbar()
    const [getTarifas] = useTarifasLazyQuery()
    const [crearRenta] = useCreateRentaMutation()
    const [descontarSaldoEasyrewards] = useDescontarSaldoEasyrewardsMutation()
    const { validarLovePoints } = useValidarLovePoints()

    /**funcion aplicar mutacion descuento de lovePoints */
    const disccountLovePoints = async (ticket_id: string, easyrewards_id: string, puntos_descontar: number) => {
        try {
            const response = await descontarSaldoEasyrewards({
                variables: {
                    easyrewards_id: easyrewards_id,
                    puntos_descontar: puntos_descontar,
                    folio_ticket: ticket_id,
                    hotel_id,
                },
            })

            if (
                response.data?.descuenta_puntos?.saldo !== undefined &&
                response.data?.descuenta_puntos?.saldo !== null
            ) {
                response.data.descuenta_puntos.saldo
            } else {
                return showSnackbar({
                    title: "Error al descontar puntos",
                    text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                    status: "error",
                })
            }
        } catch {
            return showSnackbar({
                title: "Error al descontar puntos",
                text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                status: "error",
            })
        }
    }

    return {
        reserva: undefined,
        cargarTarifas: (habitacion) =>
            getTarifas({
                variables: {
                    hotel_id,
                    tipo_habitacion_id: habitacion.habitacion.tipo_habitacion_id,
                },
            }).then((t) => filtrarTarifasRenta(t.data)),
        // Sin reserva no hay pagos previos con easyrewards
        getEasyRewardsId: () => null,
        validarTarifaPorFecha: true,
        camposBloqueados: { tipo: false, hospedajesExtra: false, earlyCheckIn: false, personas: false, nombre: false },
        conservarExtrasDeReserva: false,
        getSeccionPago: ({ isShowingPago }) => (isShowingPago ? "pago" : null),
        onSubmit: async (data, ctx) => {
            const { habitacion, tarifaSeleccionada, propinasFormatted, lovePointsAmount, getCurrentDate } = ctx
            let formattedDate = ""
            const easyRewardsId = lovePointsAmount?.id

            const isLovePointsValid = await validarLovePoints(data, ctx)
            if (!isLovePointsValid) {
                return
            }

            if (data.type === "hotel") {
                // renta para el mismo día pero con tarifa que termina antes de la fecha y hora actuales
                const endDate = setHHMMSS({
                    startDate: data.date[0],
                    newHour: tarifaSeleccionada?.hora_checkout || "",
                    isNewHourInUTC: true,
                })
                const endNextDayTarifa = endDate < getCurrentDate()

                //formato HH:mm:ss para fechaRegistro y horaCheckOut
                const fechaRegistro = new Date(data.date[0]).toTimeString().split(" ")[0]
                const horaCheckOut = tarifaSeleccionada?.hora_checkout?.split("-")[0] || "" //hora_checkout no viene convertido a utc, viene con la hora del hotel

                //formato de hora en número entero de fechaRegistro y horaCheckOut
                const horaIntRegistro = Number(fechaRegistro?.split(":")?.[0] || 0)
                const horaIntCheckOut = Number(horaCheckOut?.split(":")?.[0] || 0)

                formattedDate =
                    horaIntRegistro < horaIntCheckOut &&
                    data?.date?.[0]?.toISOString() === data?.date?.[1]?.toISOString()
                        ? endDate.toISOString()
                        : localDateToUTCString(
                              addDays({
                                  date: endDate,
                                  days: data.date[1] || endNextDayTarifa ? 1 : 0,
                              })
                          )
            }

            const fecha_checkin = localDateToUTCString(
                setHHMMSS({
                    startDate: getCurrentDate(),
                    newHour: tarifaSeleccionada?.hora_checkin || "",
                    isNewHourInUTC: true,
                })
            )

            // Se agregó el campo “fecha_checkin” cuando el tipo es hotel, en horario que se registre la venta
            const datos_renta: CreateRentaInput = {
                comentarios: [],
                fecha_checkin,
                ...(data.type === "hotel"
                    ? { fecha_fin: formattedDate, fecha_checkin: getCurrentDate().toISOString() }
                    : {}),
                habitacion_id,
                early_checkin: data.earlyCheckIn,
                ...(propinasFormatted.length
                    ? {
                          propina: {
                              usuario_id,
                              hotel_id,
                              colaborador_id: data.colaboradorRecibioPropina?.id as string,
                              detalles_pago: propinasFormatted,
                              turno_id: turno_hotel_id,
                          },
                      }
                    : {}),
                numero_personas: data.users,
                personas_extra: data.extraUsers,
                tarifa_id: data.amount,
                tipo_entrada: data.entryType,
                usuario_id,
                nombre_huesped: data.name.length ? data.name : null,
                total: data.costs.general,
                tipo_alojamiento: data.type,
                horas_extra: data.extraHours,
                hospedajes_extra:
                    data.type === "hotel" && data.date[1]
                        ? diffDays(data.date[0], data.date[1]) - 1 < 0
                            ? 0
                            : diffDays(data.date[0], data.date[1]) - 1
                        : data.extraHospedajes,
                hotel_id,

                ...(data.matricula
                    ? {
                          vehiculo: {
                              modelo: data.modelo as string,
                              matricula: data.matricula,
                              color: data.color as string,
                              marca: data.marca as string,
                          },
                      }
                    : {}),
                easyrewards_id: easyRewardsId,
                ...(data.method === PAYMENT_METHODS.consumoInterno.value && data.colaboradorConsumoInterno?.id
                    ? { consumo_interno_colaborador_id: data.colaboradorConsumoInterno.id }
                    : {}),
            }
            if (data.paymentType === "pendiente") {
                delete datos_renta.pago
                datos_renta.easyrewards_id = easyRewardsId || data.extra[0]?.easyrewards_id || ""
            } else if (data.paymentType === "parcial") {
                const pagosParciales = data.extra.filter((pago) => pago.type === "love_points")
                const totalAbonoParcial = pagosParciales.reduce((acc, pago) => acc + pago.amount, 0)

                datos_renta.pago = {
                    detallesPago: getPaymentList(
                        pagosParciales.map((pago) => ({
                            ...pago,
                            easyrewards_id: easyRewardsId,
                        }))
                    ),
                    total: totalAbonoParcial,
                    hotel_id,
                    usuario_id,
                }
            } else if (data.extra.length > 0) {
                datos_renta.pago = {
                    detallesPago: getPaymentList(
                        data.extra.map((pago) => ({
                            ...pago,
                            tipo_pago: pago.type,
                            easyrewards_id: easyRewardsId,
                        }))
                    ),
                    total: sum(data.extra.map((pago) => pago.amount)),
                    hotel_id,
                    usuario_id,
                }
            }
            await crearRenta({
                variables: {
                    datos_renta,
                },
            })
                .then((res) => {
                    showSnackbar({
                        text: `La habitación **${habitacion?.habitacion.tipo_habitacion?.nombre} ${habitacion?.habitacion.numero_habitacion}** ha sido vendida`,
                        status: "success",
                        title: "Renta de habitación exitosa",
                    })
                    const generatedTicketId = res?.data?.crear_renta?.ticket?.ticket_id[0] || ""
                    const generatedFolioRenta = res?.data?.crear_renta?.renta?.renta_id || ""

                    const hasLovePointsPm = data.extra.filter((pago) => pago.type === TiposPagos.LovePoints)

                    if (hasLovePointsPm.length > 0) {
                        hasLovePointsPm.forEach(async (pago) => {
                            const folioToSend = generatedTicketId || generatedFolioRenta
                            const result = await disccountLovePoints(folioToSend, pago.number || "", pago.amount)

                            if (result === null) {
                                showSnackbar({
                                    title: "Error al descontar puntos",
                                    text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                                    status: "error",
                                })
                            }
                        })
                    }
                    ctx.finalizarTurno(generatedFolioRenta)

                    window.location.href = generatedTicketId
                        ? `print://${generatedTicketId}_0/`
                        : `print://${generatedFolioRenta}_4/`
                    navigate("/u")
                    dispatch(toggleRoomDetailsDrawer(false))
                })
                .catch((err) => {
                    console.error(err)
                    if (
                        err.message ===
                        "No se puede realizar el cobro por concepto de early-checkin despues de la hora de checkin de la tarifa"
                    ) {
                        showSnackbar({
                            title: "Error al rentar habitación",
                            status: "error",
                            text: err.message,
                        })
                    }
                    showSnackbar({
                        title: "Error al rentar habitación",
                        status: "error",
                        text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente",
                    })
                })
        },
    }
}

export default useRentaCase
